import type { IconKind } from '@sb/design-system';

/** @feature-flag appRedesign */

export type SettingsSection =
  | 'general'
  | 'safety'
  | 'agent'
  | 'equipment'
  | 'external-control'
  | 'network'
  | 'help';

/** The route the settings sections live under. */
export const SETTINGS_PATH = '/settings';

/** The query param naming the open section, mirroring `TAB_PARAM`. */
export const SECTION_PARAM = 'section';

/**
 * The section shown when the URL names none, or names one that isn't
 * reachable.
 */
export const DEFAULT_SECTION: SettingsSection = 'general';

/** Feature flags that decide which sections an operator can reach. */
export interface SettingsSectionFlags {
  isRoutineAgentChatEnabled: boolean;
}

export interface SettingsSectionItem {
  id: SettingsSection;
  label: string;
  icon: IconKind;
  /**
   * Whether the operator can reach the section at all. Omitted means always.
   * A section that isn't reachable has no sidebar row, so nothing can open it
   * and a link naming it falls back to the default.
   */
  isReachable?: (flags: SettingsSectionFlags) => boolean;
}

/**
 * The one registry of settings sections, in sidebar order. Everything else
 * about a section — its id in the URL, its label, its icon, whether the
 * operator can reach it — is derived from this list, so adding a section is
 * this entry plus a `SettingsSection` member and a render case.
 */
export const SETTINGS_SECTION_ITEMS: SettingsSectionItem[] = [
  { id: 'general', label: 'General', icon: 'settings' },
  { id: 'safety', label: 'Safety', icon: 'shield' },
  { id: 'equipment', label: 'Equipment', icon: 'powerplug' },
  {
    id: 'external-control',
    label: 'External control',
    icon: 'connectorEthernet',
  },
  { id: 'network', label: 'Network', icon: 'wifi' },
  {
    id: 'agent',
    label: 'Agent',
    icon: 'bot',
    isReachable: (flags) => flags.isRoutineAgentChatEnabled,
  },
  { id: 'help', label: 'Help', icon: 'questionmarkCircle' },
];

export const SETTINGS_SECTIONS: SettingsSection[] = SETTINGS_SECTION_ITEMS.map(
  (item) => item.id,
);

export const SETTINGS_SECTION_LABELS = Object.fromEntries(
  SETTINGS_SECTION_ITEMS.map((item) => [item.id, item.label]),
) as Record<SettingsSection, string>;

/** Narrows a raw `?section=` value to a section this app knows. */
export function isSettingsSection(
  value: string | null,
): value is SettingsSection {
  return value != null && (SETTINGS_SECTIONS as string[]).includes(value);
}

/**
 * The sections the given flags leave reachable, in sidebar order. The sidebar,
 * the URL fallback and anything else asking "can the operator get here?" read
 * this one answer, so a flag-gated section cannot be shown in one place and
 * treated as missing in another.
 */
export function reachableSettingsSectionItems(
  flags: SettingsSectionFlags,
): SettingsSectionItem[] {
  return SETTINGS_SECTION_ITEMS.filter(
    (item) => item.isReachable?.(flags) ?? true,
  );
}

export function reachableSettingsSections(
  flags: SettingsSectionFlags,
): SettingsSection[] {
  return reachableSettingsSectionItems(flags).map((item) => item.id);
}

/**
 * Builds the link to a section, optionally carrying section-local state (the
 * Agent section's open `?skillId=`). Every settings link goes through here so
 * the query string has one shape.
 *
 * The default section is the absence of `?section=`, matching how `useTabParam`
 * treats a default tab: one screen gets one URL, so a bare `/settings` and an
 * explicit `?section=general` can't both name the same panel and be compared
 * as different places.
 */
export function settingsSectionUrl(
  section: SettingsSection,
  params?: Record<string, string>,
): string {
  const query = new URLSearchParams(
    section === DEFAULT_SECTION
      ? { ...params }
      : { [SECTION_PARAM]: section, ...params },
  ).toString();

  return query === '' ? SETTINGS_PATH : `${SETTINGS_PATH}?${query}`;
}

/**
 * The section a `/settings` query string names, or the default when it names
 * none or names something that isn't a section.
 *
 * Exported so a navigation guard can read the section straight out of the
 * `Location` objects react-router hands it instead of parsing `?section=` a
 * second way.
 */
export function settingsSectionFromSearch(search: string): SettingsSection {
  const named = new URLSearchParams(search).get(SECTION_PARAM);

  return isSettingsSection(named) ? named : DEFAULT_SECTION;
}

/**
 * True when a navigation moves between settings sections. Leaving `/settings`
 * altogether — the bottom nav, the logout route — is not a section change and
 * stays unguarded.
 *
 * The narrower of the two guard predicates. Reach for it when prompting on
 * every exit would be heavier than the state is worth; `leavesSettingsSection`
 * is the one for state an operator would be sorry to lose.
 */
export function isSettingsSectionChange(
  currentLocation: { pathname: string; search: string },
  nextLocation: { pathname: string; search: string },
): boolean {
  if (currentLocation.pathname !== nextLocation.pathname) return false;

  return (
    settingsSectionFromSearch(currentLocation.search) !==
    settingsSectionFromSearch(nextLocation.search)
  );
}

/**
 * True when a navigation takes the operator out of the settings section they
 * are in — another section, another page, the logout route.
 *
 * Section-local state (`?tab=`, the Agent section's `?skillId=`) is not a way
 * out, so a section whose draft survives its own sub-navigations doesn't
 * prompt on them.
 */
export function leavesSettingsSection(
  currentLocation: { pathname: string; search: string },
  nextLocation: { pathname: string; search: string },
): boolean {
  return (
    currentLocation.pathname !== nextLocation.pathname ||
    isSettingsSectionChange(currentLocation, nextLocation)
  );
}
